import type { MessageTemplate, MessageTemplateScope } from '@/graphql/message-templates';

/**
 * The order of a template listing, and the arithmetic behind moving a row in
 * it.
 *
 * Keystone ranks a list by `(position, created_at, id)` and this mirrors it
 * exactly, which is what lets the listing take a reorder out of the mutation's
 * own payload rather than refetching: `reorderMessageTemplates` answers with
 * every row it wrote, Apollo normalises the new `position` onto the cached
 * rows, and sorting here puts them where they now belong. Sorting by the
 * array order Keystone returned would not — that array is the *old* order and
 * nothing in the cache replaces it.
 *
 * The three keys are not decoration. `position` is not unique — there is no
 * index over it, deliberately, because a person's list is the union of two
 * scopes and a per-parent constraint would not cover the read — so two rows
 * legitimately tie, and `createdAt` then `id` are what make the order total
 * instead of leaving those two rows to shuffle between renders.
 */

/** Which of the two lists a row belongs to, which is the only thing that
 *  decides whether two rows can be reordered against each other. */
export type TemplateList = 'mine' | 'brokerage';

export function listOf(template: MessageTemplate): TemplateList {
  return template.tenantId !== null ? 'brokerage' : 'mine';
}

/**
 * The scope each list is reordered under.
 *
 * `PERSON` rather than a choice between the two personal scopes: they name the
 * same *list* to Keystone, because `me { messageTemplates }` is their union and
 * an adviser dragging a travelling template below a brokerage-bound one is
 * most of what a reorder is for. The portal never has to tell them apart —
 * which is just as well, since a row does not say which of the two it is in.
 */
export const REORDER_SCOPE: Record<TemplateList, MessageTemplateScope> = {
  mine: 'PERSON',
  brokerage: 'TENANT',
};

/** Keystone's `(position, createdAt, id)`, in that order. */
export function byListOrder(a: MessageTemplate, b: MessageTemplate): number {
  const position = (a.position ?? 0) - (b.position ?? 0);
  if (position !== 0) {
    return position;
  }
  const created = (a.createdAt ?? '').localeCompare(b.createdAt ?? '');
  return created !== 0 ? created : a.id.localeCompare(b.id);
}

export function sortForList(rows: readonly MessageTemplate[]): MessageTemplate[] {
  return [...rows].sort(byListOrder);
}

export interface MoveRequest {
  /** Every live row in the list, in its current order. The permutation is
   *  built out of this and not out of what is on screen: Keystone refuses an
   *  `orderedIds` that is not exactly the scope's live set. */
  all: readonly MessageTemplate[];
  /** The rows of that list the filters have left visible, in the same order.
   *  What "up" means is defined against these — moving a row above the one
   *  above it on screen is the only reading that matches what a reader sees.
   */
  visible: readonly MessageTemplate[];
  id: string;
  direction: 'up' | 'down';
}

/**
 * The full ordered id list that moves one row one place, or null when there is
 * nowhere for it to go.
 *
 * Two lists rather than one because a filtered listing makes them differ, and
 * both readings are needed at once: the *step* is against what is on screen —
 * moving a row above its visible neighbour, however many hidden rows sit
 * between them — and the *result* is against the whole list, because that is
 * what Keystone demands. Stepping through the full list instead would let a
 * click do nothing visible whenever the row above is filtered out, which reads
 * as a broken button.
 *
 * Null for a row at the visible end of its list, and for one that is not in
 * the list at all — a soft-deleted row, which the caller has already dropped
 * out of `all` because Keystone's live set no longer holds it.
 */
export function moveWithin({ all, visible, id, direction }: MoveRequest): string[] | null {
  const from = visible.findIndex((row) => row.id === id);
  if (from === -1) {
    return null;
  }
  const neighbour = visible[direction === 'up' ? from - 1 : from + 1];
  if (!neighbour) {
    return null;
  }
  const ids = all.map((row) => row.id);
  const moving = ids.indexOf(id);
  const target = ids.indexOf(neighbour.id);
  if (moving === -1 || target === -1) {
    return null;
  }
  const without = ids.filter((row) => row !== id);
  const at = without.indexOf(neighbour.id);
  without.splice(direction === 'up' ? at : at + 1, 0, id);
  return without;
}
